import {
  useAppData,
  useTenantedClient,
  useWhoami,
} from '@cotera/client/app/stores/org';
import { Assert } from '@cotera/utilities';
import {
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { ModelScope } from '../actions/framework/context';
import { GraphModel } from '../graph/graph.model';
import { ManifestModel } from '../manifest/model/manifest.model';
import { UserBranchesConnection } from '../models/user-branches';
import { AppSidebar } from './sidebar/sidebar';
import { Topbar } from './topbar/topbar';
import { SidebarInset, SidebarProvider } from './ui/sidebar';
import { useRegisterInWindow } from '@cotera/client/app/hooks/use-register-in-window';
import { useStorage } from '@cotera/client/app/etc/storage/provider';
import _ from 'lodash';
import { DG } from '@cotera/sdk/core';
import { useActiveTab } from '../tab-manager';
import { cn } from '../lib/utils';
import { useMemo } from 'react';
import { CsvUploadModel } from '../dataset/csv-upload/csv-upload.model';
import { useToolResolver } from '@cotera/client/app/hooks/use-tools-info';

const COMMITS_POLL_INTERVAL_SECONDS = 5;

export const useCommits = (): UseSuspenseQueryResult<DG.SavedCommit[]> => {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();

  return useSuspenseQuery({
    queryKey: ['commits', orgId],
    queryFn: async () => {
      const commits = Assert.assertOk(await client.commits.list({}));
      return commits;
    },
    refetchInterval: COMMITS_POLL_INTERVAL_SECONDS * 1000,
  });
};

const useGraphModelInit = (props: GraphModel.Props) => {
  const toolResolver = useToolResolver();

  return useSuspenseQuery({
    queryFn: async () => {
      const userBranches = await UserBranchesConnection.create(
        props.org.name,
        props.storage
      );

      return await GraphModel.create({
        ...props,
        eventEmitter: {
          dispatch: (event: CustomEvent) => {
            window.dispatchEvent(event);
          },
        },
        userBranches,
        toolResolver,
      });
    },
    queryKey: [props.org.id, 'graph-model'],
  });
};

const useManifestModelInit = () => {
  const manifestInfo = useAppData((s) => s.manifestMeta);
  return new ManifestModel(manifestInfo);
};

export function V0Layout({ children }: { children: React.ReactNode }) {
  const { data: commits } = useCommits();
  const userId = useWhoami((s) => s.whoami.id);
  const org = useWhoami((s) => s.org);
  const storage = useStorage();
  // Gross but given that we have global layout styles it's not obvious how to
  // do this without a refactor. Ideally pages would just define all of their
  // own styles through components.
  const activeTab = useActiveTab();
  const isLongTab =
    activeTab?.startsWith('/agent') || activeTab?.startsWith('/document');

  const { data: graphModel } = useGraphModelInit({
    commits,
    userId,
    org,
    storage,
  });

  const csvUploadModel = useMemo(() => {
    return new CsvUploadModel();
  }, []);

  useRegisterInWindow('COMMITS', commits);
  useRegisterInWindow('GRAPH_MODEL', graphModel);

  const manifestModel = useManifestModelInit();

  return (
    <ModelScope models={[graphModel, manifestModel, csvUploadModel]}>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset className="min-h-[100svh] overflow-hidden md:h-screen">
          <Topbar />
          <div
            className={cn(
              'flex min-h-0 flex-col h-full',
              isLongTab ? '' : 'px-4'
            )}
          >
            {children}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </ModelScope>
  );
}
